import { AccountantService, ClientService } from "../../../../services";
import {
	BackdropCircularProgress,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount
} from "../../../../common/Common";
import {
	setSelectedAccountant,
	setSelectedAccountantName,
	setSelectedActivityLogActions,
	setSelectedClient,
	setSelectedClientName,
	setSelectedStickyNavBar,
} from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CRARamittanceService } from "../../../../services/CRARamittanceService";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../../models";
import { RemittedRemittanceTransacitonsDataGrid } from "./RemittedRemittanceTransacitonsDataGrid";
import { RemittedRemittanceTransacitonsHeader } from "./RemittedRemittanceTransacitonsHeader";
import { RemittedRemittanceTransacitonsToolbar } from "./RemittedRemittanceTransacitonsToolbar";
import { TransactionsServices } from "../../../../services/DirectDeposit/TransactionsServices";
import { YearService } from "../../../../services/YearService";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IRemittedRemittanceTransacitonsSetupPage { }
const IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY = "remittedRemittance-transactions";
const CLIENT_DDL_DATA_QUERY_KEY = "client-ddl-data-record";
const ACCOUNTANT_DDL_DATA_QUERY_KEY = "accountant-ddl-data-record";
const useRemittedRemittanceTransacitons = (
	runGetListAPICall: boolean,
	accountant: string,
	client: string,
	clientType: string,
	frequency: string | null,
	remittanceYear: string | null,
	remittanceMonth: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY,
			runGetListAPICall,
			accountant,
			client,
			clientType,
			frequency,
			remittanceYear,
			params,
		],
		queryFn: () =>
			TransactionsServices.getDirectDepositRemittancesList(
				runGetListAPICall,
				400,
				accountant,
				client,
				clientType,
				frequency,
				remittanceYear,
				remittanceMonth,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetAccountantDDLItemsItems = () =>
	useQuery({
		queryKey: [ACCOUNTANT_DDL_DATA_QUERY_KEY],
		queryFn: (data) => {
			return AccountantService.getGetAccountantDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetClientsDDLItems = (accountantId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, accountantId],
		queryFn: (data) => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});

const useGetRemittanceFrequencyDDL = () =>
	useQuery({
		queryKey: ["remittance-Frequency-ddl"],
		queryFn: (data) => {
			return CRARamittanceService.getRemittanceFrequencyDDL();
		},
		refetchOnWindowFocus: false,
	});
const useYearDDL = () =>
	useQuery({
		queryKey: ["tax-tear-ddl"],
		queryFn: () => {
			return YearService.getTaxYearDDL();
		},
		refetchOnWindowFocus: false,
	});

export const RemittedRemittanceTransacitons: React.FC<IRemittedRemittanceTransacitonsSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [buttonTag, setButtonTag] = useState<any>(null);
	const [dialogButtonLabel, setDialogButtonLabel] = useState("");
	const [dialogContent, setDialogContent] = useState<any>(null);
	const [payPeriodPaymentDate, setPayPeriodPaymentDate] = useState<any>(null);

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRows, setSelectedRows] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [accountantId, setAccountantId] = useState("all");
	const [remittedRemittanceTransacitonsData, setRemittedRemittanceTransacitonsData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	const accountant = formMethods.getValues("accountant");
	const client = formMethods.getValues("client");
	const clientType = formMethods.getValues("clientType");
	const frequency = formMethods.getValues("frequency");
	const remittanceYear = formMethods.getValues("remittanceYear");
	const remittanceMonth = formMethods.getValues("remittanceMonth");


	const { isPending, data } = useRemittedRemittanceTransacitons(
		runGetListAPICall,
		accountant && accountant,
		client && client,
		clientType && clientType,
		frequency && frequency,
		remittanceYear && remittanceYear,
		remittanceMonth,
		queryParams,
		navigateToLogout
	);
	const RemittedRemittanceTransacitonsList: any | null = data;

	useEffect(() => {
		if (RemittedRemittanceTransacitonsList) {
			setRemittedRemittanceTransacitonsData(RemittedRemittanceTransacitonsList);
		}
	}, [RemittedRemittanceTransacitonsList]);

	const {
		isPending: isPendingRemittanceFrequencyDDL,
		data: remittanceFrequencyDDL,
	} = useGetRemittanceFrequencyDDL();
	const { isPending: isPendingTaxYearDDL, data: taxYearDDL } = useYearDDL();

	const updatedRemittanceFrequencyDDL = useMemo(() => {
		if (!remittanceFrequencyDDL || !Array.isArray(remittanceFrequencyDDL)) {
			return [];
		}

		let items: IDropDownListResponse[] = sortDDLByLabel(remittanceFrequencyDDL).map(
			(item: IDropDownListResponse) => ({
				label: item.label,
				value: item.value,
			})
		);

		items = items.filter((item) => item.label !== "Annual");
		items.unshift({ label: "All", value: "all" });

		return items;
	}, [remittanceFrequencyDDL]);

	const taxYearDDLItems = useMemo(() => {
		if (taxYearDDL) {
			if (
				taxYearDDL &&
				Array.isArray(taxYearDDL)
			) {
				let items: IDropDownListResponse[] =
					taxYearDDL.map(
						(item: IDropDownListResponse) => ({
							// id: item.id,
							label: item.label,
							value: item.value,
						})
					);

				items.unshift({
					label: "All",
					value: "all",
				});

				return items;
			}
			return [];
		}
	}, [taxYearDDL]);

	const remittanceMonthsDDLItems = [
		{ label: "Jan", value: 'january' },
		{ label: "Feb", value: 'february' },
		{ label: "Mar", value: 'march' },
		{ label: "Apr", value: 'april' },
		{ label: "May", value: 'may' },
		{ label: "Jun", value: 'june' },
		{ label: "Jul", value: 'july' },
		{ label: "Aug", value: 'august' },
		{ label: "Sep", value: 'september' },
		{ label: "Oct", value: 'october' },
		{ label: "Nov", value: 'november' },
		{ label: "Dec", value: 'december' },
	]

	const { isPending: isProcessing, mutate: saveRemittedRemittanceTransactions } =
		useMutation({
			mutationFn: TransactionsServices.saveRemittedRemittanceTransactions,
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} else if (res.status === 201) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// setEditRecordId(res.data.id);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingAccountantDDLItemsItems, data: accountantDDL } =
		useGetAccountantDDLItemsItems();
	const { isPending: isPendingClientsDDLItems, data: clientsDDL } =
		useGetClientsDDLItems(accountantId);

	//   after click apply button api calls with updated values and update employee report data
	// const RemittedRemittanceTransacitonsDataAPICall = (
	// 	clientIdStore: string,
	// ) => {
	// 	const fetchData = async () => {
	// 		setIsLoading(true);
	// 		setQueryParams({
	// 			search_query: "",
	// 			order_by: "firstName",
	// 			order_direction: "asc",
	// 			page: 1,
	// 			size: 25,
	// 		});
	// 		try {
	// 			const response: any = await ReportsService.getRemittedRemittanceTransacitons(
	// 				clientIdStore,
	// 				{
	// 					search_query: "",
	// 					order_by: "firstName",
	// 					order_direction: "asc",
	// 					page: 1,
	// 					size: 25,
	// 				},
	// 				navigateToLogout
	// 			);

	// 			if (response) {
	// 				setRemittedRemittanceTransacitonsData(response);
	// 			}
	// 		} catch {
	// 		} finally {
	// 			setIsLoading(false);
	// 		}
	// 	};
	// 	fetchData();
	// };




	const accountantDDLItems = useMemo(() => {
		if (accountantDDL) {
			if (
				accountantDDL &&
				Array.isArray(accountantDDL)
			) {
				let items: IDropDownListResponse[] =
					sortDDLByLabel(accountantDDL).map(
						(item: IDropDownListResponse) => ({
							// id: item.id,
							label: item.label,
							value: item.value,
						})
					);

				items.unshift({
					label: "All",
					value: "all",
				});

				return items;
			}
			return [];
		}
	}, [accountantDDL]);

	const clientsDDLItems = useMemo(() => {
		if (clientsDDL) {
			if (
				clientsDDL &&
				Array.isArray(clientsDDL)
			) {
				let items: IDropDownListResponse[] =
					sortDDLByLabel(clientsDDL).map(
						(item: IDropDownListResponse) => ({
							// id: item.id,
							label: item.label,
							value: item.value,
						})
					);

				items.unshift({
					label: "All",
					value: "all",
				});

				return items;
			}
			return [];
		}
	}, [clientsDDL]);

	const onAccountantSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		setAccountantId(event.value)
	}

	//   after click apply button api calls with updated values and update employee report data
	const remittedRemittanceTransacitonsDataAPICall = (
		accountant: string,
		client: string,
		clientType: string,
		frequency: null | string,
		remittanceYear: null | string,
		remittanceMonth: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await TransactionsServices.getDirectDepositRemittancesList(
					true,
					400,
					accountant,
					client,
					clientType,
					frequency,
					remittanceYear,
					remittanceMonth,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setRemittedRemittanceTransacitonsData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		remittedRemittanceTransacitonsDataAPICall(
			values.accountant,
			values.client,
			values.clientType,
			values.frequency,
			values.remittanceYear,
			values.remittanceMonth,
		);
	};

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const onClickHandler = async (clickedRow: any) => {
		setIsLoading(true);
		try {
			const accountantDetails = await AccountantService.getById(
				clickedRow.accountantId,
				navigateToLogout
			);

			if (accountantDetails && accountantDetails.id) {
				dispatch(setSelectedAccountant({ id: accountantDetails.id }));
				dispatch(setSelectedAccountant(accountantDetails));
				dispatch(setSelectedAccountantName({ accTitle: accountantDetails.accTitle }));

				const clientDetails = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (clientDetails && clientDetails.id) {
					dispatch(
						setSelectedClient({
							...clientDetails,
							superUserFieldsShownInClientSetup: true,
						})
					);
					dispatch(setSelectedClientName({ clientName: clientDetails.legalName }));
					navigate("/clients/dashboard");
				}
			} else {
				const response = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (response && response.id) {
					dispatch(setSelectedClient(response));
					navigate("/clients/dashboard");
				}
			}
		} catch (error) {
			// handle/log error as needed
		} finally {
			setIsLoading(false);
		}
	};


	const upadtedRemittedRemittanceTransactionsListItems = useMemo(() => {
		return (
			remittedRemittanceTransacitonsData &&
			remittedRemittanceTransacitonsData.items &&
			remittedRemittanceTransacitonsData?.items.map((item: any) => ({
				...item,
				clientName: (
					<span
						// to={`/clients/dashboard`}
						className="no-underline"
						style={{ color: theme.palette.primary.light, cursor: "pointer" }}
						onClick={() => onClickHandler(item)}
					>
						{item.clientName}
					</span>
				),
			}))
		);
	}, [remittedRemittanceTransacitonsData]);

	const onAmountRemittedButtonClickedHandler = () => {
		if (selectedRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("amount-remitted")
			setDialogButtonLabel("Amount Remitted")
			setDialogContent(`Are you sure you want to remitted amount of (${selectedRows.length}) selected ${selectedRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to process!",
				severity: "error"
			})
		}
	}
	const onDeleteButtonClickedHandler = () => {
		if (selectedRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("delete")
			setDialogButtonLabel("Delete")
			setDialogContent(`Are you sure you want to delete (${selectedRows.length}) selected ${selectedRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to delete!",
				severity: "error"
			})
		}
	}
	const onSaveButtonClickedHandler = () => {
		if (selectedRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("save")
			setDialogButtonLabel("Save")
			setDialogContent(`Are you sure you want to save (${selectedRows.length}) selected ${selectedRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to save!",
				severity: "error"
			})
		}
	}
	const onSendEmailButtonClickedHandler = () => {
		if (selectedRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("send-email")
			setDialogButtonLabel("Send Email")
			setDialogContent(`Are you sure you want to send email to (${selectedRows.length}) selected ${selectedRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to send email!",
				severity: "error"
			})
		}
	}
	const onForceRemitButtonClickedHandler = () => {
		if (selectedRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("force-remit")
			setDialogButtonLabel("Force Remit")
			setDialogContent(`Are you sure you want to force remit (${selectedRows.length}) selected ${selectedRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to force remit!",
				severity: "error"
			})
		}
	}
	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const dialogButtonClickedHandler = async (tag: string | null) => {
		if (tag === 'save') {
			saveRemittedRemittanceTransactions(selectedRows)
		} else if (tag === 'delete') {
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.deleteRemittedRemittanceTransactions(selectedRows);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		} else if (tag === 'amount-remitted') {
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.amountRemittedRemittanceTransactions(selectedRows);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		} else if (tag === 'send-email') {
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.sendEmailRemittedRemittanceTransactions(selectedRows);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		} else if (tag === 'force-remit') {
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.forceRemitRemittedRemittanceTransactions(selectedRows);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_COLLECTION_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		}
	}

	const onDetailsTableRowDeleteClicked = (params: any) => {

		alert(params.amount)
	}
	const onDetailsTableRowEditClicked = (params: any) => {

		alert(params.amount)
	}
	const expandedLabels = ["Transactions", "Remittance"];
	const expandedRemittedRemittanceOrSetupItem = "Remitted";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Remittance" &&
				item.label === expandedRemittedRemittanceOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedRemittedRemittanceOrSetupItem && "/direct-deposit/transactions/remittance/remitted",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);



	return (
		<>
			{/* <h3>Salary Report</h3> */}
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Remitted"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<RemittedRemittanceTransacitonsHeader
						clientsDDL={clientsDDLItems}
						accountantDDL={accountantDDLItems}
						frequencyDDL={updatedRemittanceFrequencyDDL}
						remittanceYearDDL={taxYearDDLItems}
						remittanceMonthDDL={remittanceMonthsDDLItems}
						onAccountantSelectionChangeHandler={onAccountantSelectionChangeHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)} />
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isLoading ||
						isPendingClientsDDLItems ||
						isProcessing ||
						isPendingTaxYearDDL ||
						isPendingRemittanceFrequencyDDL
					}
					testId="remittedRemittance-transactions"
				/>


				<RemittedRemittanceTransacitonsDataGrid
					columns={
						remittedRemittanceTransacitonsData?.headerColumns
							? remittedRemittanceTransacitonsData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// remittedRemittanceTransacitonsData?.headerColumns ?? []
					}
					// completeHeaderColumnsList={
					// 	remittedRemittanceTransacitonsData
					// 		&& remittedRemittanceTransacitonsData
					// 			?.totalHeaderColumns &&
					// 		Object.keys(remittedRemittanceTransacitonsData.totalHeaderColumns).length !== 0 /// comment  for use hardcoded data change later
					// 		? remittedRemittanceTransacitonsData.totalHeaderColumns
					// 		: {}
					// 	// remittedRemittanceTransacitonsData ?? []
					// }
					rows={
						remittedRemittanceTransacitonsData && upadtedRemittedRemittanceTransactionsListItems
							? upadtedRemittedRemittanceTransactionsListItems
							: []
						// remittedRemittanceTransacitonsData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={true}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						remittedRemittanceTransacitonsData && remittedRemittanceTransacitonsData.total
							? remittedRemittanceTransacitonsData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search..."}
					pageTitle={"in process collection"}
					selectedRows={(value: any) => setSelectedRows(value)}
					onDetailsTableRowEditClicked={onDetailsTableRowEditClicked}
					onDetailsTableRowDeleteClicked={onDetailsTableRowDeleteClicked}
				/>
				{remittedRemittanceTransacitonsData && remittedRemittanceTransacitonsData.items && remittedRemittanceTransacitonsData.items.length > 0 && (
					<RemittedRemittanceTransacitonsToolbar
						onAmountRemittedButtonClickedHandler={onAmountRemittedButtonClickedHandler}
						onDeleteButtonClickedHandler={onDeleteButtonClickedHandler}
						onSaveButtonClickedHandler={onSaveButtonClickedHandler}
						onSendEmailButtonClickedHandler={onSendEmailButtonClickedHandler}
						onForceRemitButtonClickedHandler={onForceRemitButtonClickedHandler}
					/>
				)}
			</Grid>

			<EnhancedDialog
				dialogTitle={"Please Confirm"}
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseHandler}
				open={openDialog}
				dialogContent={dialogContent}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label={dialogButtonLabel}
							severity={
								buttonTag === 'amount-remitted' ? EnhancedButtonPrimeReactSeverity.Success :
									buttonTag === 'delete' ? EnhancedButtonPrimeReactSeverity.Danger :
										buttonTag === 'save' ? EnhancedButtonPrimeReactSeverity.Success :
											EnhancedButtonPrimeReactSeverity.Secondary}
							raised
							onClickHandler={() => dialogButtonClickedHandler(buttonTag)}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
